import { Injectable } from '@angular/core';

import type { AnalyticsMode } from '@recruitee/analytics';
import { AnalyticsBase } from '@recruitee/analytics';
import type { SourcesType } from '@recruitee/candidate-types';
import type { StageCategory } from '@recruitee/stages-types';
import type { SnakeToCamelCase } from '@recruitee/utils';

export enum TrackCandidateTarget {
  JOB = 'job',
  TALENT_POOL = 'talentPool',
}

export enum TrackUnhireTrigger {
  CHANGE_STAGE = 'changeStage',
  REMOVE_FROM_JOB = 'removeFromJob',
}

export enum TrackProceedOrigin {
  CARD_MOVE = 'cardMove',
  STAGE_SELECT = 'stageSelect',
  PROCEED_BUTTON = 'proceedButton',
}

export enum TrackAddCandidateMethod {
  MANUALLY = 'manually',
  RESUME = 'resume',
  IMPORT = 'import',
}

export enum TrackEditedHiringDetail {
  HIRED_AT = 'hiredAt',
  JOB_STARTS_AT = 'jobStartsAt',
}

export enum TrackNavigateProfilesTrigger {
  BUTTON = 'button',
  KEYBOARD = 'keyboard',
}

export type CamelCasedStageCategoryValue = SnakeToCamelCase<`${StageCategory}`>;

export type TrackSortCandidatesTrigger = 'dropdown' | 'column_header';
export type TrackSortCandidatesOrder = 'ascending' | 'descending';

@Injectable()
export class CandidateAnalytics extends AnalyticsBase {
  public readonly namespace = 'Candidates';
  public readonly requireViewContext = true;

  public addTextingConsent(): void {
    this.track('AddTextingConsent', {});
  }

  public withdrawTextingConsent(): void {
    this.track('WithdrawTextingConsent', {});
  }

  public textingSend(): void {
    this.track('SendSMS', {});
  }

  public trackAddCandidate(method: TrackAddCandidateMethod, candidatesCount: number): void {
    this.track('AddCandidate', { method, candidatesCount });
  }

  public trackProceed(
    origin: TrackProceedOrigin,
    mode: AnalyticsMode,
    candidatesCount: number = 1,
    toStageType?: CamelCasedStageCategoryValue,
  ): void {
    this.track(
      'Proceed',
      {
        origin,
        mode,
        candidatesCount,
        ...(toStageType ? { toStageType } : {}),
      },
      { integrations: { All: false, Mixpanel: true, Intercom: true } },
    );
  }

  public trackDisqualify(mode: AnalyticsMode, candidatesCount: number = 1): void {
    this.track(
      'Disqualify',
      { mode, candidatesCount },
      { integrations: { All: false, Mixpanel: true, Intercom: true } },
    );
  }

  public trackRequalify(mode: AnalyticsMode, candidatesCount: number = 1): void {
    this.track('Requalify', { mode, candidatesCount });
  }

  public trackAssign(
    mode: AnalyticsMode,
    target: TrackCandidateTarget[],
    candidatesCount: number = 1,
  ): void {
    this.track('Assign', { mode, target: target, candidatesCount });
  }

  public trackRemove(
    mode: AnalyticsMode,
    target: TrackCandidateTarget[],
    candidatesCount: number = 1,
  ): void {
    this.track('Remove', { mode, target: target, candidatesCount });
  }

  public trackDelete(mode: AnalyticsMode, candidatesCount: number = 1): void {
    this.track('Delete', { mode, candidatesCount });
  }

  public trackMarkAsHired(dirtyFields: {
    editedHireDate: boolean;
    withStartDate: boolean;
    multipleJobs: boolean;
    disqualify: boolean;
    inShareLink: boolean;
    removedFromShareLink: boolean;
    sourcingOrigin: null | string;
    sources: string[];
    tags: string[];
    source: SourcesType;
    referrer: null | string;
  }): void {
    this.track('MarkAsHired', dirtyFields);
  }

  public trackUnhire(mode: AnalyticsMode, trigger: TrackUnhireTrigger): void {
    this.track('Unhire', { mode, trigger });
  }

  public trackViewHiringDetails(): void {
    this.track('ViewHiringDetails');
  }

  public trackEditedHiringDetails(edited: TrackEditedHiringDetail[]): void {
    this.track('EditedHiringDetails', { edited });
  }

  public trackNavigateProfiles(trigger: TrackNavigateProfilesTrigger): void {
    this.track('NavigateProfiles', { trigger });
  }

  public trackMergeCandidates(candidatesCount: number): void {
    this.track('MergeProfiles', { candidatesCount });
  }

  public trackAdjustColumns({
    selectedField,
    enabled,
  }: {
    selectedField: string;
    enabled: boolean;
  }): void {
    this.track('AdjustColumns', { selectedField, enabled });
  }

  public trackSortCandidates({
    sortBy,
    order,
    trigger,
  }: {
    sortBy: string;
    order: TrackSortCandidatesOrder;
    trigger: TrackSortCandidatesTrigger;
  }): void {
    this.track('SortCandidates', { sortBy, order, trigger });
  }

  public trackEditWorkLocation(assignedLocations: number[]): void {
    this.track('EditWorkLocation', { assignedLocations });
  }

  public trackProfileSectionToggle(section: string, action: 'collapse' | 'expand'): void {
    this.track('ProfileSectionCollapseExpand', { section, action });
  }

  public trackReveal(): void {
    this.track('Reveal');
  }

  public trackViewed(): void {
    this.track('Viewed');
  }
}
